import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { LoadContentDocument, ProcessContentDocument } from 'generated/resources/graphql'
import { isEqual } from 'lodash'
import { autorun } from 'mobx'

import { observer } from 'mobx-react'

import { useOne } from 'modules/ai/lib/useOne'
import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { LMSModuleAvailabilityWall } from 'modules/trainings/components/LMSModuleAvailabilityWall'
import ContentBuilder from 'modules/trainings/components/content/Builder'
import { contentRegistry } from 'modules/trainings/components/content/ContentRegistry'
import { ContentPreview } from 'modules/trainings/components/content/preview/ContentPreview'
import { ContentItem } from 'modules/trainings/lib/content/ContentItem'
import {
  contentFromAi,
  contentToAi,
  ModuleCollectionType,
} from 'modules/trainings/lib/content/aiTypes'
import { graphqlContentMapper } from 'modules/trainings/lib/content/mappers'

const ContentBuilderPage = observer(() => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { id: trainingId } = useParams()
  const session = useCurrentSession()
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isPreviewMode, setIsPreviewMode] = useState(false)

  // Load content modules for the current training.
  const { data, loading, refetch } = useQuery(LoadContentDocument, {
    variables: {
      trainingId: String(trainingId),
    },
  })

  // Save the content modules for the current training.
  const [processContent, { loading: isSaving }] = useMutation(ProcessContentDocument, {
    extractorFn(mutationData) {
      return mutationData.trainingsMutations.processContentTrainingsTraining
    },
  })

  // Convert the content modules from GraphQL to a ModuleCollection, which is used by the builder.
  const collection = useMemo(() => {
    const modules = data?.trainings.contentModules
    if (loading || !modules) {
      return undefined
    }
    return graphqlContentMapper.fromGraphQL(modules, String(trainingId))
  }, [trainingId, data, loading])

  // Initial state for the co-creation agent (used for both AI-enabled and AI-disabled modes)
  const initialAgentState = useMemo<ModuleCollectionType>(
    () => ({
      ...(collection ? contentToAi(collection) : { modules: [] }),
    }),
    [collection]
  )

  // useOne handles both AI-enabled and AI-disabled modes transparently
  const {
    coCreationData: agentState,
    setCoCreationData: setAgentState,
    isThinking,
  } = useOne<ModuleCollectionType>(['trainingContent'], initialAgentState, {
    autoOpenChat: true,
    initialMessage: i18n.t('trainings.one.initial_message'),
  })
  const agentStateRef = useRef(agentState)
  const bootstrapAgentStateRef = useRef<ModuleCollectionType | null>(null)
  // Guards the AI-to-collection sync from running before the initial server
  // content has been loaded and applied to the agent state. Without this flag,
  // a stale or empty agent state can call collection.clear() and produce a
  // false "unsaved changes" signal, which—if saved—would overwrite previously
  // saved content with an empty payload. (FCT-50757)
  const hasBootstrappedRef = useRef(false)

  useEffect(() => {
    agentStateRef.current = agentState
  }, [agentState])

  useEffect(() => {
    if (!collection || !agentState || hasBootstrappedRef.current) return

    const loadedState = contentToAi(collection)
    const hasLoadedContent = loadedState.modules.length > 0

    if (hasLoadedContent) {
      bootstrapAgentStateRef.current = loadedState
      setAgentState({ ...loadedState })
    }
    // Mark bootstrap complete regardless of whether the server has content, so
    // the sync effect is allowed to run for both new and existing courses.
    hasBootstrappedRef.current = true
  }, [agentState, collection, setAgentState])

  // Sync from AI agent to local collection when agent updates content
  useEffect(() => {
    // Do not sync until the initial server content has been bootstrapped into
    // the agent state. Running this before bootstrap would call
    // collection.clear() with stale/empty agent state. (FCT-50757)
    if (!agentState || !collection || !hasBootstrappedRef.current) return

    if (bootstrapAgentStateRef.current) {
      if (isEqual(agentState, bootstrapAgentStateRef.current)) {
        bootstrapAgentStateRef.current = null
      }
      return
    }

    if (isEqual(contentToAi(collection), agentState)) {
      // No changes from AI, no need to update collection or mark as unsaved
      return
    }

    contentFromAi(collection, agentState)
    setHasUnsavedChanges(true)
  }, [agentState, collection])

  // Sync from local collection to AI agent when collection changes
  useEffect(() => {
    return autorun(() => {
      if (setAgentState && collection) {
        const newState = contentToAi(collection)
        if (!isEqual(newState, agentStateRef.current)) {
          setAgentState({ ...newState })
        }
      }
    })
  }, [setAgentState, collection])

  if (!collection || loading) {
    return <Loading expand />
  }

  const handleSave = () => {
    if (!isSaving && !isThinking) {
      // Guard: prevent saving an empty collection when the server already has
      // content. This is a last-resort safety net for FCT-50757: if a bootstrap
      // race somehow still produces a false "unsaved changes" signal with an
      // empty collection, we block the overwrite, show a message, and restore
      // the last known good server state instead.
      const serverHadContent = (data?.trainings.contentModules?.length ?? 0) > 0
      if (collection.empty() && serverHadContent) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('trainings.content.save.empty_overwrite_blocked'),
        })
        refetch()
        setHasUnsavedChanges(false)
        return
      }

      // Use GraphQL mapper to convert from domain to GraphQL input
      const contentModules = graphqlContentMapper.toGraphQL(collection)

      processContent({
        variables: {
          authorId: session.employee.id,
          companyId: session.company.id,
          trainingId: collection.trainingId,
          contentModules,
        },
      }).then((result) => {
        const handled = handleResult(result, () => {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('trainings.content.save.error'),
          })
        })

        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('trainings.content.save.success'),
          })
          setHasUnsavedChanges(false)
        }
      })
    }
  }

  const handleDiscard = () => {
    refetch()
    setHasUnsavedChanges(false)
  }

  return (
    <LMSModuleAvailabilityWall>
      {isPreviewMode && (
        <ContentPreview collection={collection} onClose={() => setIsPreviewMode(false)} />
      )}
      <ResponsivePage.Body
        variant="fullscreen"
        height="full"
        onActionClick={(action: string) => {
          if (action === 'preview') {
            setIsPreviewMode(true)
          } else if (action === 'back') {
            return navigate(
              navigation.trainings.list.details_revamp.view.overview.fullPath({
                id: String(trainingId),
              })
            )
          }
        }}
      >
        <ContentBuilder
          tocItems={collection.modules.map((contentModule) => ({
            id: contentModule.uuid,
            icon: contentRegistry.fetch(contentModule, 'icon'),
            label: contentModule.title,
            onClick: () => collection.setSelected(contentModule.uuid),
            children: contentModule.blocks.map((block) => ({
              id: block.uuid,
              label: block.title,
              icon: contentRegistry.fetch(block, 'icon'),
              onClick: () => collection.setSelected(block.uuid),
            })),
          }))}
          selectedItem={collection.selectedItem}
          trainingId={collection.trainingId}
          hasUnsavedChanges={hasUnsavedChanges}
          isThinking={isThinking}
          onReorder={(orderedStructure) => {
            collection.reorder(orderedStructure)
            setHasUnsavedChanges(true)
          }}
          onAddContent={({ contentItem }: { contentItem: ContentItem }) => {
            collection.addItem(contentItem)
            setHasUnsavedChanges(true)
          }}
          onDeleteContent={(uuid: string) => {
            collection.deleteItem(uuid)
            setHasUnsavedChanges(true)
          }}
          onContentChange={() => {
            setHasUnsavedChanges(true)
          }}
          onSave={handleSave}
          onDiscard={handleDiscard}
        />
      </ResponsivePage.Body>
    </LMSModuleAvailabilityWall>
  )
})

export default ContentBuilderPage
